import {
  UzBadge,
  UzButton,
  UzDescriptionList,
  UzDrawer,
  UzErrorState,
  UzSpinner,
} from '@utazzo/ui';
import { ExternalLink, RotateCw, UserPlus } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';

import {
  ASSIGNMENT_METHOD_LABEL,
  CHANNEL_LABEL,
  canBeAssigned,
  FORWARD_LABEL,
  FORWARD_TONE,
  LEAD_STATUS_LABEL,
  SOURCE_LABEL,
  formatDateTime,
  formatPhone,
} from '../labels';
import { lpsErrorText, useLpsLead, useRetryLpsLead } from '../use-lps';

import { LpsDeliveryDrawer } from './LpsDeliveryDrawer';

import type { LpsLeadItem } from '../lps.api';

/** Display order is this object's key order. */
const ATTRIBUTION_LABEL: Record<string, string> = {
  adPlatform: 'Platform',
  adIsOrganic: 'Organic (no ad)',
  adAccountId: 'Ad account id',
  campaignName: 'Campaign',
  campaignId: 'Campaign id',
  adsetName: 'Ad set',
  adsetId: 'Ad set id',
  adName: 'Ad',
  adId: 'Ad id',
  adPlacement: 'Placement',
  formName: 'Form',
  formId: 'Form id',
  adPageId: 'Facebook page id',
  utmSource: 'utm_source',
  utmMedium: 'utm_medium',
  utmCampaign: 'utm_campaign',
  utmTerm: 'utm_term',
  utmContent: 'utm_content',
  utmId: 'utm_id',
  gclid: 'gclid (Google click)',
  gbraid: 'gbraid',
  wbraid: 'wbraid',
  fbclid: 'fbclid (Meta click)',
  msclkid: 'msclkid (Bing click)',
  fbc: '_fbc',
  fbp: '_fbp',
  gaClientId: 'GA client id',
  landingUrl: 'Landed on',
  pageUrl: 'Submitted on',
  referrer: 'Referrer',
  touchAt: 'Visit time',
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="space-y-2">
    <h3 className="text-xs font-semibold tracking-wide text-zinc-500 uppercase">{title}</h3>
    {children}
  </section>
);

export const LpsLeadDrawer = ({
  leadId,
  onClose,
  canWrite,
  onAssign,
}: {
  leadId: string | null;
  onClose: () => void;
  canWrite: boolean;
  onAssign: (lead: LpsLeadItem) => void;
}): React.JSX.Element => {
  const lead = useLpsLead(leadId);
  const [deliveryId, setDeliveryId] = React.useState<string | null>(null);
  const retry = useRetryLpsLead();
  const data = lead.data;
  const canRetry =
    canWrite && data && (data.forward.status === 'FAILED' || data.forward.status === 'PARKED');
  const canAssign = canWrite && data && canBeAssigned(data);

  return (
    <>
      <LpsDeliveryDrawer deliveryId={deliveryId} onClose={() => setDeliveryId(null)} />
      <UzDrawer
        open={Boolean(leadId)}
        onOpenChange={(open) => !open && onClose()}
        title={data?.fullName ?? 'Inbound lead'}
        description={
          data
            ? `${SOURCE_LABEL[data.source]} · ${data.connection.name} · ${formatDateTime(data.receivedAt)}`
            : undefined
        }
        footer={
          canAssign ? (
            <UzButton leftIcon={<UserPlus className="size-4" />} onClick={() => onAssign(data)}>
              Assign agent
            </UzButton>
          ) : canRetry ? (
            <UzButton
              leftIcon={<RotateCw className="size-4" />}
              loading={retry.isPending}
              onClick={() => retry.mutate(data.id)}
            >
              Retry sending to CRM
            </UzButton>
          ) : undefined
        }
      >
        {lead.isLoading && (
          <div className="flex justify-center py-10">
            <UzSpinner />
          </div>
        )}
        {lead.isError && (
          <UzErrorState
            size="compact"
            title="Could not load this lead"
            description={lpsErrorText(lead.error, 'Try again.')}
            onRetry={() => void lead.refetch()}
          />
        )}
        {data && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-2">
              <UzBadge tone={data.status === 'DUPLICATE' ? 'warning' : 'secondary'}>
                {LEAD_STATUS_LABEL[data.status]}
              </UzBadge>
              <UzBadge tone={FORWARD_TONE[data.forward.status]}>
                {FORWARD_LABEL[data.forward.status]}
              </UzBadge>
              {data.forward.crmLeadId && (
                <Link
                  to={`/leads/${data.forward.crmLeadId}`}
                  className="text-primary inline-flex items-center gap-1 text-sm font-medium hover:underline"
                >
                  {data.forward.crmLeadReference} <ExternalLink className="size-3.5" />
                </Link>
              )}
            </div>

            <Section title="Contact">
              <UzDescriptionList
                layout="inline"
                density="compact"
                items={[
                  { term: 'Name', description: data.fullName ?? '-' },
                  {
                    term: 'Phone',
                    description: <span className="font-mono">{formatPhone(data.phone)}</span>,
                  },
                  {
                    term: 'As sent',
                    description: (
                      <span className="font-mono text-zinc-500">{data.phoneRaw ?? '-'}</span>
                    ),
                  },
                  { term: 'Email', description: data.email ?? '-' },
                  {
                    term: 'Message',
                    description: <span className="whitespace-pre-wrap">{data.message ?? '-'}</span>,
                  },
                ]}
              />
            </Section>

            <Section title="Trip">
              <UzDescriptionList
                layout="inline"
                density="compact"
                items={[
                  { term: 'Destination', description: data.trip.destination ?? '-' },
                  {
                    term: 'Travellers',
                    description:
                      [
                        data.trip.adults != null ? `${data.trip.adults} adults` : null,
                        data.trip.children != null ? `${data.trip.children} children` : null,
                      ]
                        .filter(Boolean)
                        .join(', ') || '-',
                  },
                  { term: 'Budget', description: data.trip.budget ?? '-' },
                ]}
              />
            </Section>

            <Section title="Where it came from">
              <UzDescriptionList
                layout="inline"
                density="compact"
                items={[
                  {
                    term: 'Channel',
                    description: `${CHANNEL_LABEL[data.channel.channel]}${
                      data.channel.platform ? ` · ${data.channel.platform}` : ''
                    }`,
                  },
                  ...Object.keys(ATTRIBUTION_LABEL)
                    .filter((k) => data.attribution[k])
                    .map((k) => ({
                      term: ATTRIBUTION_LABEL[k]!,
                      description: (
                        <span className="break-all">
                          {k === 'touchAt'
                            ? formatDateTime(data.attribution[k] ?? null)
                            : data.attribution[k]}
                        </span>
                      ),
                    })),
                ]}
              />
            </Section>

            <Section title="CRM hand-off">
              <UzDescriptionList
                layout="inline"
                density="compact"
                items={[
                  { term: 'State', description: FORWARD_LABEL[data.forward.status] },
                  {
                    term: 'Assigned to',
                    description: data.assignment.agentName
                      ? `${data.assignment.agentName}${data.assignment.method ? ` (${ASSIGNMENT_METHOD_LABEL[data.assignment.method]})` : ''}`
                      : '-',
                  },
                  { term: 'Attempts', description: data.forward.attempts },
                  ...(data.forward.forwardedAt
                    ? [{ term: 'Sent', description: formatDateTime(data.forward.forwardedAt) }]
                    : []),
                  ...(data.forward.nextAttemptAt
                    ? [
                        {
                          term: 'Next try',
                          description: formatDateTime(data.forward.nextAttemptAt),
                        },
                      ]
                    : []),
                  ...(data.forward.error
                    ? [
                        {
                          term:
                            data.forward.status === 'SKIPPED' ||
                            data.forward.status === 'AWAITING_ASSIGNMENT'
                              ? 'Why'
                              : 'Last error',
                          description: (
                            <span className="break-words text-zinc-600">{data.forward.error}</span>
                          ),
                        },
                      ]
                    : []),
                  ...(data.externalId
                    ? [
                        {
                          term: 'Provider id',
                          description: <span className="font-mono text-xs">{data.externalId}</span>,
                        },
                      ]
                    : []),
                ]}
              />
            </Section>

            {/* Raw provider data is admin-only; the API sends none to others. */}
            {!data.rawHidden && (
              <>
                <Section title="Webhook delivery">
                  {data.delivery ? (
                    <button
                      type="button"
                      className="text-primary text-sm font-medium hover:underline"
                      onClick={() => setDeliveryId(data.delivery!.id)}
                    >
                      Received {formatDateTime(data.delivery.receivedAt)} - view the request as sent
                    </button>
                  ) : (
                    <p className="text-sm text-zinc-500">
                      Not in the delivery log (received before it existed, or older than 90 days).
                    </p>
                  )}
                </Section>

                <Section title="Raw payload">
                  <pre className="max-h-80 overflow-auto rounded-md bg-zinc-950 p-3 font-mono text-[11px] leading-relaxed text-zinc-100">
                    {JSON.stringify(data.raw, null, 2)}
                  </pre>
                </Section>
              </>
            )}
          </div>
        )}
      </UzDrawer>
    </>
  );
};
